import { X, Download, Loader2, CheckCircle, AlertCircle, FileDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as API from '../../../api';

const limit = 2000000;

const DownloadModal = (props:any) => {
  const [loading, setLoading] = useState(true);
  const [url, setURL] = useState(null);
  const [error, setError] = useState(false);

  // Reset state when jobId changes (FIX: limpa estado anterior)
  useEffect(() => {
    if (!props.jobId) return;
    
    // Limpa estados antes de processar novo job
    setLoading(true);
    setURL(null);
    setError(false);
    
    checkJobRoutine();
  }, [props.jobId]);

  // Reset state when url from props changes
  useEffect(() => {
    if (props.url && props.url !== '') {
      setLoading(false);
      setURL(props.url);
    }
  }, [props.url]);

  // Initial cleanup
  useEffect(() => {
    setError(false);
    setURL(null);
  }, []);

  async function checkJobRoutine() {
    try {
      setTimeout(async () => {
        try {
          const request = await API.checkJobId(props.jobId);
          if (!request) {
            setLoading(false);
            setError(true);
            return;
          }
          const response = request.data;
          if (response.data.status === 'progress' || response.data.status === 'started') {
            checkJobRoutine();
          } else {
            if (response.data.status === 'finish') {
              const json = JSON.parse(response.data.data);
              setLoading(false);
              if (json.error) {
                setError(true);
              } else {
                setURL(json.url);
              }
            }
          }
        } catch (error) {
          console.error('Request failed', error);
          setLoading(false);
          setError(true);
        }
      }, 5000);
    } catch (error) {
      console.error('Error in setTimeout', error);
      setLoading(false);
      setError(true);
    }
  }

  // Setup visual content
  const getStatusContent = () => {
    if (props.counter > limit) {
      return {
        icon: <AlertCircle className="w-16 h-16 text-red-500" />,
        title: 'Tivemos um problema',
        message: 'Limite de 2 milhões de registros excedido. Faça um filtro mais específico.',
        iconBg: 'bg-red-50',
      };
    } else if (loading && !props.error && !error) {
      return {
        icon: <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />,
        title: 'Gerando relatório',
        message: 'Aguarde, essa solicitação pode levar alguns minutos',
        iconBg: 'bg-blue-50',
      };
    } else if (props.error || error) {
      return {
        icon: <AlertCircle className="w-16 h-16 text-red-500" />,
        title: 'Erro ao gerar relatório',
        message: 'Tente novamente mais tarde',
        iconBg: 'bg-red-50',
      };
    }
    return {
      icon: <CheckCircle className="w-16 h-16 text-green-500" />,
      title: 'Relatório gerado com sucesso!',
      message: 'Seu download está pronto',
      iconBg: 'bg-green-50',
    };
  };

  const status = getStatusContent();
  
  // Gera URL final do download (FIX: sempre recalcula baseado no estado atual)
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const downloadUrl = props.url || url;
  const fullDownloadUrl = downloadUrl ? `${apiUrl}${downloadUrl}` : null;

  const canShowDownload = !loading && !error && !props.error && props.counter <= limit && fullDownloadUrl;
  const canClose = !loading || props.error || error || props.counter > limit;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden max-w-md w-full mx-auto"
    >
      {/* Header */}
      <div className="px-6 py-5 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-700 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <motion.div
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <FileDown className="w-5 h-5 text-white" />
          </motion.div>
          <h3 className="text-lg font-bold text-white">{status.title}</h3>
        </div>
        {canClose && (
          <motion.button
            whileHover={{ scale: 1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => props.onClose()}
            className="text-white/80 hover:text-white hover:bg-white/20 rounded-xl p-2 transition-all duration-200"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </motion.button>
        )}
      </div>

      {/* Content */}
      <div className="px-6 py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={loading ? 'loading' : error ? 'error' : 'success'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center text-center space-y-6"
          >
            <motion.div
              animate={loading ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 2, repeat: loading ? Infinity : 0 }}
              className={`p-4 ${status.iconBg} rounded-2xl shadow-inner`}
            >
              {status.icon}
            </motion.div>
            
            <div className="space-y-2">
              <p className="text-slate-600 text-base font-medium">{status.message}</p>
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center justify-center space-x-2 text-xs text-slate-400"
                >
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                  <span>Processando...</span>
                </motion.div>
              )}
            </div>

            {canShowDownload && (
              <motion.a
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={fullDownloadUrl}
                download
                className="mt-4 inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all duration-200 gap-2"
              >
                <Download className="w-5 h-5" />
                Baixar Relatório
              </motion.a>
            )}

            {(error || props.error || props.counter > limit) && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => props.onClose()}
                className="mt-4 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all duration-200"
              >
                Fechar
              </motion.button>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Loading Progress Bar */}
      {loading && (
        <div className="h-1 bg-slate-100 overflow-hidden">
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="h-full  bg-gradient-to-r from-transparent via-blue-500 to-transparent"
          />
        </div>
      )}
    </motion.div>
  );
};

export default DownloadModal;